import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import API from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getViolationColor } from '../types/caseForm';
import { formatCaseDate, formatIsoDate } from '../utils/formatDateValue';
import { auditToEvent, AuditLog } from '../components/ActivityCard';
import { DocumentTextIcon, ClockIcon, CheckBadgeIcon, ChevronRightIcon, InboxIcon } from '@heroicons/react/24/outline';
import { enumLabel } from '../utils/enumLabel';

interface DashboardStats {
  totalCases: number;
  totalApprovedCases: number;
  totalVictims: number;
  primaryVsSecondary: { primary: number; secondary: number };
  perStatus: Record<string, number>;
  perIpriRegion: { LAC: number; ASIA: number; AFRICA: number; unclassified: number };
  approvedThisYear: { year: number; total: number; LAC: number; ASIA: number; AFRICA: number };
  approvedLastYear: { year: number; total: number; LAC: number; ASIA: number; AFRICA: number };
}

interface RecentCase {
  id: string;
  caseNumber: string;
  incidentDate?: string | null;
  country: string;
  region?: string;
  status: string;
  violationTypes?: string[];
  assignedTo?: { firstName: string; lastName: string } | null;
  createdAt: string;
}

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft', FOR_VALIDATION: 'For Validation', IN_RESEARCH: 'In Research', FOR_APPROVAL: 'For Approval',
  APPROVED: 'Approved', REJECTED: 'Rejected', ARCHIVED: 'Archived',
};
const PIPELINE_STATUSES = ['DRAFT', 'FOR_VALIDATION', 'IN_RESEARCH', 'FOR_APPROVAL'];
const DECIDED_STATUSES = ['APPROVED', 'REJECTED', 'ARCHIVED'];
// Meters use a neutral ramp; the legend beneath carries the meaning (colour rule: no decorative hues).
const RAMP = ['bg-gray-800 dark:bg-gray-200', 'bg-gray-600 dark:bg-gray-400', 'bg-gray-500 dark:bg-gray-500', 'bg-gray-400 dark:bg-gray-600', 'bg-gray-300 dark:bg-gray-700', 'bg-gray-200 dark:bg-gray-800', 'bg-gray-100 dark:bg-gray-900'];
const NEUTRAL_PILL = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';

// Per-card load state so one failed request doesn't blank the whole page.
type Load<T> = { data: T | null; error: string | null; loading: boolean };
const idle = <T,>(): Load<T> => ({ data: null, error: null, loading: true });

const Card: React.FC<{ title: React.ReactNode; subtitle?: string; className?: string; children: React.ReactNode }> = ({ title, subtitle, className, children }) => (
  <div className={`bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg ${className ?? ''}`}>
    <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 font-heading">{title}</h3>
      {subtitle && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const CardState: React.FC<{ error: string | null; loading: boolean; children: React.ReactNode }> = ({ error, loading, children }) => {
  if (loading) return <div className="p-5 animate-pulse space-y-2"><div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-2/3" /><div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/2" /></div>;
  if (error) return <p className="p-5 text-sm text-gray-500 dark:text-gray-400">{error}</p>;
  return <>{children}</>;
};

const Meter: React.FC<{ entries: [string, number][]; total: number; hrefs?: Record<string, string> }> = ({ entries, total, hrefs }) => (
  <>
    <div className="flex rounded-full overflow-hidden h-2.5 mb-3 bg-gray-100 dark:bg-gray-700" role="img" aria-label={entries.map(([l, c]) => `${l}: ${c}`).join(', ')}>
      {entries.map(([label, count], i) => (
        <div key={label} className={RAMP[i % RAMP.length]} style={{ width: total > 0 ? `${(count / total) * 100}%` : '0%' }} title={`${label}: ${count}`} />
      ))}
    </div>
    <ul className="space-y-1.5">
      {entries.map(([label, count], i) => (
        <li key={label} className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <span className={`w-2.5 h-2.5 rounded-full ${RAMP[i % RAMP.length]}`} aria-hidden="true" />
            {hrefs?.[label] ? <Link to={hrefs[label]} className="hover:underline">{label}</Link> : label}
          </span>
          <span className="tabular-nums font-medium text-gray-900 dark:text-gray-100">{count}</span>
        </li>
      ))}
    </ul>
  </>
);

// A queue row: what is waiting, for whom, and where to go. Counts come from the scoped list
// endpoint (limit=1, read pagination.total) so they always agree with the Cases page.
interface QueueItem { key: string; label: string; href: string; count: number | null; roles?: string[] }

// The greeting: time of day, then one line a day about why the record matters. Rotates by date
// so it doesn't become wallpaper, and stays factual rather than cheerful about the subject.
const timeGreeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};
const DAILY_LINES = [
  'Every case documented here is a community\'s account placed on the record, in their words.',
  'Indigenous Peoples defend a quarter of the world\'s land. This record keeps count of what that costs them.',
  'A violation that is documented can be named, followed up and answered. That is the work.',
  'Behind each case number is a family, a territory and a decision someone still has to make.',
  'Careful documentation is how a pattern becomes evidence, and evidence becomes accountability.',
  'Free, prior and informed consent is a right, not a courtesy. The cases here show where it was denied.',
  'Land, language, ceremony and self-government: the rights in this record are the ones that keep peoples whole.',
];
const dailyLine = () => {
  const d = new Date();
  const dayOfYear = Math.floor((d.getTime() - new Date(d.getFullYear(), 0, 0).getTime()) / 86400000);
  return DAILY_LINES[dayOfYear % DAILY_LINES.length];
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role ?? '';
  const isReviewer = role === 'RESEARCHER' || role === 'APPROVER' || role === 'ADMIN';
  const isApprover = role === 'APPROVER' || role === 'ADMIN';
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [stats, setStats] = useState<Load<DashboardStats>>(idle());
  const [recent, setRecent] = useState<Load<RecentCase[]>>(idle());
  const [activity, setActivity] = useState<Load<AuditLog[]>>(idle());

  useEffect(() => {
    const defs: { key: string; label: string; href: string; count: () => Promise<number>; show: boolean }[] = [
      { key: 'mine', label: 'Assigned to me', href: '/cases?assignedTo=me', show: isReviewer,
        count: () => API.get('/api/cases?assignedTo=me&limit=1').then((r) => r.data.pagination?.total ?? 0) },
      { key: 'validation', label: 'Awaiting validation, unassigned', href: '/cases?status=FOR_VALIDATION&assignedTo=unassigned', show: isReviewer,
        count: () => API.get('/api/cases?status=FOR_VALIDATION&assignedTo=unassigned&limit=1').then((r) => r.data.pagination?.total ?? 0) },
      { key: 'approval', label: 'Awaiting approval', href: '/cases?status=FOR_APPROVAL', show: isApprover,
        count: () => API.get('/api/cases?status=FOR_APPROVAL&limit=1').then((r) => r.data.pagination?.total ?? 0) },
      { key: 'duplicates', label: 'Duplicate matches to review', href: '/scraper?section=duplicates', show: isReviewer,
        count: () => API.get('/api/dedup/pending-matches').then((r) => (Array.isArray(r.data) ? r.data.length : 0)) },
      { key: 'drafts', label: 'My drafts', href: '/cases?status=DRAFT&view=my', show: true,
        count: () => API.get('/api/cases?status=DRAFT&submittedBy=me&limit=1').then((r) => r.data.pagination?.total ?? 0) },
    ];
    const active = defs.filter((d) => d.show);
    setQueue(active.map((d) => ({ key: d.key, label: d.label, href: d.href, count: null })));
    Promise.all(active.map((d) => d.count().catch(() => null))).then((counts) => {
      setQueue(active.map((d, i) => ({ key: d.key, label: d.label, href: d.href, count: counts[i] })));
      setQueueLoading(false);
    });

    API.get('/api/dashboard/stats')
      .then((r) => setStats({ data: r.data, error: null, loading: false }))
      .catch(() => setStats({ data: null, error: 'Statistics could not be loaded.', loading: false }));
    API.get('/api/cases?limit=10')
      .then((r) => setRecent({ data: r.data.cases || [], error: null, loading: false }))
      .catch(() => setRecent({ data: null, error: 'Recent cases could not be loaded.', loading: false }));
    API.get('/api/audit-logs?limit=15')
      .then((r) => setActivity({ data: r.data.logs || [], error: null, loading: false }))
      .catch(() => setActivity({ data: null, error: 'Recent activity could not be loaded.', loading: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const s = stats.data;
  const trend = (() => {
    if (!s) return null;
    const diff = s.approvedThisYear.total - s.approvedLastYear.total;
    if (diff === 0) return `same as in ${s.approvedLastYear.year}`;
    return `${Math.abs(diff)} ${diff > 0 ? 'more' : 'fewer'} than in ${s.approvedLastYear.year} (${s.approvedLastYear.total})`;
  })();
  const entriesFor = (keys: string[]): [string, number][] =>
    s ? keys.filter((k) => (s.perStatus[k] ?? 0) > 0).map((k) => [STATUS_LABEL[k] || k, s.perStatus[k]]) : [];
  const pipelineEntries = entriesFor(PIPELINE_STATUSES);
  const decidedEntries = entriesFor(DECIDED_STATUSES);
  const pipelineTotal = pipelineEntries.reduce((sum, [, c]) => sum + c, 0);
  const decidedTotal = decidedEntries.reduce((sum, [, c]) => sum + c, 0);
  const regionEntries: [string, number][] = s
    ? [['LAC', s.perIpriRegion.LAC], ['Asia', s.perIpriRegion.ASIA], ['Africa', s.perIpriRegion.AFRICA], ...(s.perIpriRegion.unclassified > 0 ? [['Unclassified', s.perIpriRegion.unclassified] as [string, number]] : [])]
    : [];
  const regionTotal = regionEntries.reduce((sum, [, c]) => sum + c, 0);

  const statusHrefs: Record<string, string> = Object.fromEntries(Object.entries(STATUS_LABEL).map(([k, v]) => [v, `/cases?status=${k}`]));
  const regionHrefs: Record<string, string> = { LAC: '/cases?status=APPROVED&ipriRegion=LAC', Asia: '/cases?status=APPROVED&ipriRegion=ASIA', Africa: '/cases?status=APPROVED&ipriRegion=AFRICA' };
  const year = s?.approvedThisYear.year ?? new Date().getFullYear();

  const events = (activity.data ?? []).map((l) => ({ log: l, ev: auditToEvent(l) })).filter((x) => x.ev);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 font-heading">
          {timeGreeting()}{user?.firstName ? `, ${user.firstName}` : ''}
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{dailyLine()}</p>
      </div>

      {/* My work — what is waiting for this user, each row a link into the filtered list */}
      {queue.length > 0 && (
        <Card title={<span className="inline-flex items-center gap-2"><InboxIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />My work</span>} className="mb-6">
          <ul className="divide-y divide-gray-100 dark:divide-gray-700 sm:grid sm:grid-cols-2 lg:grid-cols-5 sm:divide-y-0 sm:divide-x">
            {queue.map((q) => {
              const empty = q.count === 0;
              return (
                <li key={q.key}>
                  <Link
                    to={q.href}
                    className={`flex items-center justify-between gap-3 px-5 py-4 h-full hover:bg-gray-50 dark:hover:bg-gray-700/40 ${empty ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}
                  >
                    <span className="min-w-0">
                      <span className="block text-2xl font-semibold tabular-nums leading-tight">
                        {queueLoading && q.count === null ? <span className="inline-block h-7 w-8 bg-gray-200 dark:bg-gray-600 rounded animate-pulse align-middle" /> : q.count ?? '—'}
                      </span>
                      <span className="block text-xs mt-0.5">{q.label}</span>
                    </span>
                    <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {/* Top stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-6">
        {[
          { label: 'Total cases', value: s?.totalCases, icon: DocumentTextIcon, sub: s ? `${s.totalVictims} victims recorded` : undefined, href: '/cases' },
          { label: `Approved, submitted in ${year}`, value: s?.approvedThisYear.total, icon: CheckBadgeIcon, sub: trend ?? undefined, href: `/cases?status=APPROVED&year=${year}` },
          { label: 'Primary · Secondary data', value: s ? `${s.primaryVsSecondary.primary} · ${s.primaryVsSecondary.secondary}` : undefined, icon: DocumentTextIcon, sub: 'Field documentation · public reporting', href: '/cases?dataSource=primary' },
        ].map((t) => (
          <Link key={t.label} to={t.href} className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg p-5 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700/40">
            <div className="flex-shrink-0 rounded-md p-3 bg-gray-100 dark:bg-gray-700">
              <t.icon className="h-6 w-6 text-gray-600 dark:text-gray-300" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t.label}</p>
              {stats.loading ? (
                <div className="h-7 w-16 mt-1 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
              ) : stats.error ? (
                <p className="text-sm text-gray-400">—</p>
              ) : (
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{t.value}</p>
              )}
              {t.sub && !stats.loading && !stats.error && <p className="text-xs text-gray-500 dark:text-gray-400">{t.sub}</p>}
            </div>
          </Link>
        ))}
      </div>

      {/* Meters */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card title="Cases by status">
          <CardState loading={stats.loading} error={stats.error}>
            <div className="p-5 space-y-5">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">In the pipeline</h4>
                  <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">{pipelineTotal}</span>
                </div>
                {pipelineEntries.length ? <Meter entries={pipelineEntries} total={pipelineTotal} hrefs={statusHrefs} /> : <p className="text-sm text-gray-500 dark:text-gray-400">Nothing in progress.</p>}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Decided</h4>
                  <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">{decidedTotal}</span>
                </div>
                {decidedEntries.length ? <Meter entries={decidedEntries} total={decidedTotal} hrefs={statusHrefs} /> : <p className="text-sm text-gray-500 dark:text-gray-400">No decided cases yet.</p>}
              </div>
            </div>
          </CardState>
        </Card>
        <Card title="Approved cases by IPRI region">
          <CardState loading={stats.loading} error={stats.error}>
            <div className="p-5"><Meter entries={regionEntries} total={regionTotal} hrefs={regionHrefs} /></div>
          </CardState>
        </Card>
      </div>

      {/* Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Recent cases" subtitle="Latest submissions">
          <CardState loading={recent.loading} error={recent.error}>
            {(recent.data ?? []).length === 0 ? (
              <p className="p-5 text-sm text-gray-500 dark:text-gray-400">No cases yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700 max-h-96 overflow-y-auto">
                {(recent.data ?? []).map((c) => (
                  <li key={c.id}>
                    <div
                      role="link"
                      tabIndex={-1}
                      onClick={() => navigate(`/cases/${c.id}`)}
                      className="px-5 py-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link to={`/cases/${c.id}`} onClick={(e) => e.stopPropagation()} className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:underline">{c.caseNumber}</Link>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${c.status === 'REJECTED' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' : NEUTRAL_PILL}`}>{STATUS_LABEL[c.status] || c.status}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          {(c.violationTypes || []).map((v) => (
                            <span key={v} className={`inline-block px-1.5 py-0.5 text-[11px] rounded-full border ${getViolationColor(v).soft}`}>{enumLabel(v)}</span>
                          ))}
                        </div>
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {c.country}{c.region ? `, ${c.region}` : ''}
                          {c.incidentDate ? ` · ${formatCaseDate(c.incidentDate)}` : ''}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                        {c.assignedTo ? `${c.assignedTo.firstName} ${c.assignedTo.lastName}` : <span className="italic">Unassigned</span>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardState>
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700">
            <Link to="/cases" className="text-sm font-medium text-gray-700 dark:text-gray-300 underline hover:text-gray-900 dark:hover:text-gray-100">All cases</Link>
          </div>
        </Card>

        <Card title={<span className="inline-flex items-center gap-2"><ClockIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />Recent activity</span>} subtitle="Latest changes across cases">
          <CardState loading={activity.loading} error={activity.error}>
            {events.length === 0 ? (
              <p className="p-5 text-sm text-gray-500 dark:text-gray-400">No recent activity.</p>
            ) : (
              <ol className="divide-y divide-gray-100 dark:divide-gray-700 max-h-96 overflow-y-auto">
                {events.map(({ log, ev }) => (
                  <li key={log.id} className="px-5 py-3 text-sm">
                    <div className="text-gray-800 dark:text-gray-200">
                      <span className="font-medium text-gray-900 dark:text-gray-100">{log.user ? `${log.user.firstName} ${log.user.lastName}` : 'System'}</span>
                      {' '}<span className="text-gray-700 dark:text-gray-300">{ev!.title.charAt(0).toLowerCase() + ev!.title.slice(1)}</span>
                      {ev!.detail && <span className="text-gray-500 dark:text-gray-400"> · {ev!.detail}</span>}
                      {log.case && (
                        <>
                          {' '}<span className="text-gray-500 dark:text-gray-400">on</span>{' '}
                          <Link to={`/cases/${log.case.id}`} className="font-medium text-gray-900 dark:text-gray-100 hover:underline">{log.case.caseNumber}</Link>
                        </>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {log.user?.role ? `${log.user.role.charAt(0) + log.user.role.slice(1).toLowerCase()} · ` : ''}
                      <time dateTime={log.createdAt} title={formatIsoDate(log.createdAt) || log.createdAt}>{formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}</time>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </CardState>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
